import { useQuery } from '@tanstack/react-query';
import { ChevronDown, LayoutTemplate } from 'lucide-react';
import { useState } from 'react';
import { TASK_TEMPLATE_TEXTS } from '@till-erp/collab/values';
import { foldSearchText } from '@till-erp/platform/lists';
import { apiFetch, type TaskTemplateEntry, type TaskTemplateList } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { SearchMenu, SearchMenuNote } from './ui/search-menu.tsx';

/**
 * Vorlagen für Aufgaben (Story 23.12, FR-161, EXPERIENCE §4.22; Entwurf Variante A
 * `ux-till-erp/entwuerfe-2026-10-29-vorlagen-erinnerung/`): Liste laden und das Suchmenü „Aus Vorlage …“ im Blatt
 * „Neue Aufgabe“. Die Seite Einstellungen › Aufgaben › Vorlagen (nur Admin) liegt in `pages/task-templates.tsx`.
 */

export const TASK_TEMPLATE_UI_TEXTS = {
  ...TASK_TEMPLATE_TEXTS,
  fromTemplate: 'Aus Vorlage …',
  chooser: 'Vorlage wählen',
  search: 'Vorlage suchen',
  empty: 'Noch keine Vorlagen. Der Admin legt sie unter Einstellungen › Aufgaben › Vorlagen an.',
  noMatch: 'Keine Vorlage passt.',
  loading: 'Lädt …',
  /** „4 Punkte · fällig in 5 Arbeitstagen“ unter dem Namen im Menü. */
  meta: (t: Pick<TaskTemplateEntry, 'checklist' | 'dueInWorkdays'>) =>
    [t.checklist.length > 0 ? TASK_TEMPLATE_TEXTS.points(t.checklist.length) : null, TASK_TEMPLATE_TEXTS.dueIn(t.dueInWorkdays)].filter(Boolean).join(' · '),
} as const;

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'] as const;

/** `2027-03-01` → „Mo 01.03.2027“ (Vorschau und Band). */
export function weekdayDate(date: string): string {
  const [y, m, d] = date.split('-');
  return `${WEEKDAYS[new Date(`${date}T12:00:00Z`).getUTCDay()]} ${d}.${m}.${y}`;
}

export const taskTemplatesKey = (archived: boolean) => ['tasks', 'templates', archived ? 'mit-archiv' : 'aktiv'] as const;

/** Vorlagen mit Fälligkeit „bei Anlage heute“; `archived` (nur Admin) liefert auch archivierte. */
export function useTaskTemplates(archived = false, enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: taskTemplatesKey(archived),
    queryFn: () => apiFetch<TaskTemplateList>(getToken, `/api/v1/tasks/templates${archived ? '?archived=true' : ''}`),
    enabled,
  });
}

/**
 * „Aus Vorlage …“ (alle Personen): Knopf im Kopf des Blatts, öffnet ein Suchmenü mit Name und „4 Punkte · fällig in 5
 * Arbeitstagen“. ↑/↓ markieren, Enter übernimmt, Esc schließt. Die Liste lädt erst beim ersten Öffnen.
 */
export function TemplatePicker({ onChoose, className }: { onChoose: (template: TaskTemplateEntry) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false);
  const [query, setQuery] = useState('');
  const templates = useTaskTemplates(false, opened);
  const items = templates.data?.items ?? [];
  const folded = foldSearchText(query.trim());
  const matches = items.filter((t) => folded === '' || foldSearchText(`${t.name} ${t.title}`).includes(folded));

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <SearchMenu
      open={open}
      onOpenChange={(v) => {
        if (v) {
          setOpened(true);
          setOpen(true);
        } else close();
      }}
      label={TASK_TEMPLATE_UI_TEXTS.chooser}
      className="w-picker-w"
      listClassName="max-h-80"
      testId="vorlage-wahl"
      query={query}
      onQueryChange={setQuery}
      searchLabel={TASK_TEMPLATE_UI_TEXTS.search}
      listLabel={TASK_TEMPLATE_UI_TEXTS.chooser}
      options={matches.map((t) => ({
        key: t.id,
        testId: 'vorlage-option',
        className: 'min-h-11 flex-col items-stretch justify-center gap-0.5 py-space-2',
        content: (
          <>
            <span className="truncate font-medium">{t.name}</span>
            <span className="type-meta truncate text-ink-3">{TASK_TEMPLATE_UI_TEXTS.meta(t)}</span>
          </>
        ),
      }))}
      onChoose={(i) => {
        const t = matches[i];
        if (!t) return;
        close();
        onChoose(t);
      }}
      trigger={(props) => (
        <button
          type="button"
          {...props}
          data-testid="aus-vorlage"
          className={cn(
            'inline-flex h-9 cursor-pointer items-center gap-space-2 rounded-field border border-line bg-card px-space-3 text-row text-ink-2 hover:border-field-line hover:text-ink aria-expanded:border-brand aria-expanded:text-ink pointer-coarse:h-11',
            className,
          )}
        >
          <LayoutTemplate size={16} strokeWidth={1.75} aria-hidden className="flex-none text-ink-3" />
          {TASK_TEMPLATE_UI_TEXTS.fromTemplate}
          <ChevronDown size={14} strokeWidth={2} aria-hidden className="flex-none text-ink-3" />
        </button>
      )}
    >
      {templates.isPending && opened ? <SearchMenuNote>{TASK_TEMPLATE_UI_TEXTS.loading}</SearchMenuNote> : null}
      {templates.isSuccess && matches.length === 0 ? <SearchMenuNote>{items.length === 0 ? TASK_TEMPLATE_UI_TEXTS.empty : TASK_TEMPLATE_UI_TEXTS.noMatch}</SearchMenuNote> : null}
      {templates.error ? <SearchMenuNote alert>{templates.error.message}</SearchMenuNote> : null}
    </SearchMenu>
  );
}
